import React, { useMemo } from "react";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";

// Import educational assistant animation
const MagicLampLottie = dynamic(() => import("../MagicLampLottie"), {
  ssr: false,
});

interface AssistantTriggerProps {
  onClick: () => void;
  isOpen: boolean;
}

// Memoize the sparkle particles component
const SparkleParticles = React.memo(() => {
  // Memoize particle positions for stability
  const particlePositions = useMemo(() => {
    return Array(10)
      .fill(0)
      .map((_, i) => {
        // Calculate angle for circular distribution
        const angle = (i * (Math.PI * 2)) / 10;
        // Add some randomness to distance from center
        const distance = 40 + Math.random() * 15;

        return {
          left: `${Math.cos(angle) * distance + 50}%`,
          top: `${Math.sin(angle) * distance + 50}%`,
          duration: 2 + Math.random() * 1.5,
          size: Math.random() * 5 + 2,
          delay: i * 0.2,
          // Cosmic colors matching Assistant.tsx
          color: [
            "#0ccff8", // Cyan
            "#57B2FC", // Light Blue
            "#4c72fa", // Blue
            "#005BF6", // Royal Blue
            "#0638ee", // Deep Blue
            "#60E8C6", // Teal
            "#39d9f9", // Light Cyan
            "#ffffff", // White (like stars)
            "#b4e6ff", // Light Blue (matching boxShadow in Star component)
            "#0ccff8", // Cyan again
          ][i % 10],
          // Some particles use twinkle animation instead
          animationType: Math.random() > 0.6 ? "twinkle" : "pulse",
        };
      });
  }, []);

  return (
    <>
      {particlePositions.map((pos, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full shadow-lg"
          style={{
            left: pos.left,
            top: pos.top,
            width: `${pos.size}px`,
            height: `${pos.size}px`,
            backgroundColor: pos.color,
            boxShadow: `0 0 8px ${pos.color}`,
          }}
          initial={{ opacity: 0 }}
          animate={
            pos.animationType === "twinkle"
              ? {
                  opacity: [0.2, 0.9, 0.2],
                  scale: [0.8, 1.5, 0.8],
                }
              : {
                  opacity: [0.2, 0.8, 0.2],
                  scale: [1, 1.5, 1],
                  x: [0, Math.random() * 15 - 7.5, 0],
                  y: [0, Math.random() * 15 - 7.5, 0],
                }
          }
          transition={{
            duration: pos.duration,
            delay: pos.delay,
            repeat: Infinity,
            repeatType: "loop",
            ease: "easeInOut",
          }}
        />
      ))}
    </>
  );
});
SparkleParticles.displayName = "SparkleParticles";

// Memoize the button background particles
const ButtonBackgroundParticles = React.memo(() => {
  // Memoize particle positions for stability
  const particlePositions = useMemo(() => {
    return Array(15)
      .fill(0)
      .map(() => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: `${Math.random() * 3 + 1}px`,
        height: `${Math.random() * 3 + 1}px`,
        color: [
          "rgba(12, 207, 248, 0.9)", // Bright Cyan
          "rgba(87, 178, 252, 0.8)", // Light Blue
          "rgba(76, 114, 250, 0.8)", // Blue
          "rgba(0, 91, 246, 0.8)", // Royal Blue
          "rgba(96, 232, 198, 0.8)", // Teal
          "rgba(255, 255, 255, 0.7)", // White (like stars)
        ][Math.floor(Math.random() * 6)],
        animationDuration: `${Math.random() * 4 + 2}s`,
        animationDelay: `${Math.random() * 2}s`,
        boxShadow:
          Math.random() > 0.7 ? "0 0 6px rgba(255, 255, 255, 0.8)" : "none", // Some particles glow more
        scale: Math.random() > 0.8 ? 1.5 : 1, // Some particles are larger
      }));
  }, []);

  return (
    <>
      {particlePositions.map((style, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: style.left,
            top: style.top,
            width: style.width,
            height: style.height,
            backgroundColor: style.color,
            borderRadius: "50%",
            boxShadow: style.boxShadow,
            transform: `scale(${style.scale})`,
            animation: `float ${style.animationDuration} ${style.animationDelay} infinite alternate`,
          }}
        />
      ))}
    </>
  );
});
ButtonBackgroundParticles.displayName = "ButtonBackgroundParticles";

// Memoize the AssistantTrigger component
const AssistantTrigger = React.memo(
  ({ onClick, isOpen }: AssistantTriggerProps) => {
    return (
      <motion.button
        id="assistant-trigger-button"
        className="fixed bottom-20 md:bottom-6 right-6 z-50 flex items-center justify-center"
        onClick={onClick}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {/* DESIGN 3: Knowledge Illumination Theme */}

        {/* Main warm glow effect */}
        <motion.div
          className="absolute inset-0 rounded-full bg-gradient-to-r from-[#FFD700]/40 via-[#FFA500]/40 to-[#FF8C00]/40"
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            repeatType: "loop",
            ease: "easeInOut",
          }}
        />

        {/* Light ray particles */}
        <motion.div
          className="absolute inset-[-15px] opacity-60"
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          {Array(12)
            .fill(0)
            .map((_, i) => (
              <motion.div
                key={i}
                className="absolute left-1/2 top-1/2 h-[1.5px] origin-left"
                style={{
                  width: "35%",
                  backgroundColor: i % 2 === 0 ? "#FFCC33" : "#FFA500",
                  transform: `rotate(${i * 30}deg)`,
                  boxShadow:
                    i % 2 === 0 ? "0 0 8px #FFCC33" : "0 0 5px #FFA500",
                  opacity: 0.7,
                }}
                animate={{
                  opacity: [0.4, 0.7, 0.4],
                  width: ["35%", "40%", "35%"],
                }}
                transition={{
                  duration: 3,
                  delay: i * 0.3,
                  repeat: Infinity,
                  repeatType: "mirror",
                }}
              />
            ))}
        </motion.div>

        {/* Main button with warm golden gradient background */}
        <div className="relative flex items-center justify-center w-16 h-16 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-[#8B4513] to-[#654321] shadow-lg overflow-hidden border border-[#FFD700]/60">
          {/* Animated light particles in button background */}
          <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-[#8B4513] to-[#654321]">
            {/* Light particle effects */}
            {Array(20)
              .fill(0)
              .map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    width: `${Math.random() * 3 + 1}px`,
                    height: `${Math.random() * 3 + 1}px`,
                    backgroundColor: [
                      "rgba(255, 215, 0, 0.8)", // Gold
                      "rgba(255, 165, 0, 0.7)", // Orange
                      "rgba(255, 140, 0, 0.7)", // Dark Orange
                      "rgba(255, 255, 224, 0.9)", // Light Yellow
                      "rgba(255, 222, 173, 0.8)", // Navajo White
                    ][Math.floor(Math.random() * 5)],
                    boxShadow: "0 0 4px rgba(255, 215, 0, 0.8)",
                  }}
                  animate={{
                    opacity: [0.3, 0.8, 0.3],
                    y: [0, -Math.random() * 10, 0],
                  }}
                  transition={{
                    duration: 2 + Math.random() * 3,
                    delay: Math.random() * 2,
                    repeat: Infinity,
                    repeatType: "loop",
                  }}
                />
              ))}

            {/* Central glow effect */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#FFCC33]/30 via-[#FFD700]/20 to-[#FFA500]/30 blur-lg"></div>
            </div>
          </div>

          {/* Icon container with lightbulb icon */}
          <motion.div
            className="relative z-10 text-[#FFD700] w-full h-full flex items-center justify-center"
            whileHover={{
              rotate: 5,
              scale: 1.05,
            }}
            whileTap={{ scale: 0.95 }}
          >
            {isOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-9 w-9 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-9 w-9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18h6"></path>
                <path d="M10 22h4"></path>
                <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path>
                <motion.path
                  d="M12 2v1"
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                ></motion.path>
                <motion.path
                  d="M3.04 11h1"
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                ></motion.path>
                <motion.path
                  d="M19.96 11h1"
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
                ></motion.path>
                <motion.path
                  d="M4.5 4.5l1 1"
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
                ></motion.path>
                <motion.path
                  d="M18.5 4.5l-1 1"
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.8 }}
                ></motion.path>
              </svg>
            )}
          </motion.div>
        </div>

        {/* Floating knowledge elements */}
        <motion.div
          className="absolute -top-1 -right-1 flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-[#FFA500] to-[#FF8C00] shadow-lg border border-[#FFD700]/60"
          animate={{
            y: [0, -3, 0],
            boxShadow: [
              "0 0 6px rgba(255, 215, 0, 0.6)",
              "0 0 12px rgba(255, 165, 0, 0.7)",
              "0 0 6px rgba(255, 215, 0, 0.6)",
            ],
          }}
          transition={{
            y: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            },
            boxShadow: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            },
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 20h9"></path>
            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
          </svg>
        </motion.div>

        {/* Attention-grabbing pulse that appears occasionally */}
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-[#FFD700]"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: [0, 0.7, 0],
            scale: [0.8, 1.3, 1.6],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: "loop",
            repeatDelay: 4,
            ease: "easeOut",
          }}
        />

        {/* Add global styles for animations */}
        <style jsx global>{`
          @keyframes float {
            0% {
              transform: translateY(0) translateX(0);
              opacity: 0.3;
            }
            50% {
              opacity: 0.9;
            }
            100% {
              transform: translateY(-10px) translateX(5px);
              opacity: 0.5;
            }
          }

          @keyframes glow {
            0%,
            100% {
              opacity: 0.7;
              box-shadow: 0 0 10px #ffd700;
            }
            50% {
              opacity: 1;
              box-shadow: 0 0 20px #ffd700, 0 0 30px #ffa500;
            }
          }
        `}</style>
      </motion.button>
    );
  }
);

// Add display name for enhanced debugging
AssistantTrigger.displayName = "AssistantTrigger";

export default AssistantTrigger;
